import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { formatDate, getLocalDateString } from '../../../utils/dateUtils';
import Swal from 'sweetalert2';
import { useVendorAuth } from '../../../context/VendorAuthContext';
import './MyLeads.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function MyLeads() {
    const navigate = useNavigate();
    const { vendor } = useVendorAuth();

    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const [filters, setFilters] = useState({ name: '', email: '', category: '', startDate: '', endDate: '', status: '' });
    const [suggestedCount, setSuggestedCount] = useState(0);
    const [downloading, setDownloading] = useState(false);

    // Dynamically load Tailwind CSS CDN on mount
    useEffect(() => {
        // Set config to disable global resets (preflight) so sidebar menu stays visible
        window.tailwind = {
            config: {
                corePlugins: {
                    preflight: false
                }
            }
        };

        const script = document.createElement('script');
        script.src = "https://cdn.tailwindcss.com";
        script.id = "tailwind-cdn-loader";
        document.head.appendChild(script);

        return () => {
            const scriptNode = document.getElementById("tailwind-cdn-loader");
            if (scriptNode) {
                scriptNode.remove();
            }
            // Clean up config
            delete window.tailwind;

            // Clean up style tags generated by Tailwind CDN to prevent leak
            const styleNodes = document.querySelectorAll('style');
            styleNodes.forEach(node => {
                if (node.innerHTML.includes('--tw-') || node.innerHTML.includes('tailwind')) {
                    node.remove();
                }
            });
        };
    }, []);

    const fetchLeads = async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: pagination.limit,
                name: filters.name,
                email: filters.email,
                category: filters.category,
                startDate: filters.startDate,
                endDate: filters.endDate,
                status: filters.status
            };
            const response = await apiClient.get('/vendors/leads', { params });
            if (response.data.success) {
                setLeads(response.data.data);
                setPagination(response.data.pagination);
            }
        } catch (err) {
            setError("Failed to fetch leads");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSuggestedCount = async () => {
        try {
            const response = await apiClient.get('/recommendations/vendor/suggested-customers', {
                params: { page: 1, limit: 1 }
            });
            if (response.data.success) {
                setSuggestedCount(response.data.totalItems || response.data.data?.length || 0);
            }
        } catch (err) {
            console.error("Failed to fetch suggested count:", err);
        }
    };

    // Initial and filtered load
    useEffect(() => {
        fetchLeads(1);
        fetchSuggestedCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.category, filters.startDate, filters.endDate, filters.status]);

    useEffect(() => {
        const refreshAfterRecommendationUnlock = () => {
            fetchLeads(pagination.page);
            fetchSuggestedCount();
        };
        window.addEventListener('suggested-customer-unlocked', refreshAfterRecommendationUnlock);
        return () => window.removeEventListener('suggested-customer-unlocked', refreshAfterRecommendationUnlock);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page]);

    // Debounce name/email input filters
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (filters.name !== '' || filters.email !== '') {
                fetchLeads(1);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.name, filters.email]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchLeads(newPage);
        }
    };

    const handleUnlockLead = (lead) => {
        Swal.fire({
            title: 'Unlock Lead?',
            text: "This will consume 1 lead balance from your account.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#08a88a',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, unlock it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await apiClient.post(`/vendors/leads/${lead.id}/unlock`);
                    if (res.data.success) {
                        window.dispatchEvent(new CustomEvent('lead-balance-updated', {
                            detail: { availableLeads: res.data.available_leads }
                        }));
                        Swal.fire(
                            'Unlocked!',
                            'The lead has been successfully unlocked.',
                            'success'
                        );
                        fetchLeads(pagination.page);
                    }
                } catch (err) {
                    Swal.fire(
                        'Error!',
                        err.response?.data?.message || "Failed to unlock lead",
                        'error'
                    );
                }
            }
        });
    };

    // Client-side export to Excel compatible CSV
    const handleDownload = async () => {
        if (downloading) return;
        try {
            setDownloading(true);
            // Fetch all matching data (setting high limit to get all matching leads)
            const params = {
                page: 1,
                limit: 9999,
                name: filters.name,
                email: filters.email,
                category: filters.category,
                startDate: filters.startDate,
                endDate: filters.endDate,
                status: filters.status
            };
            const response = await apiClient.get('/vendors/leads', { params });
            if (response.data.success) {
                const dataToDownload = response.data.data;
                const headers = ['SR No.', 'Customer Name', 'Phone', 'Email', 'Category', 'City', 'Date Sent', 'Status'];
                const rows = dataToDownload.map((lead, index) => [
                    index + 1,
                    lead.name || 'Guest',
                    lead.mobile || 'N/A',
                    lead.email || 'N/A',
                    lead.category || 'N/A',
                    lead.city || 'N/A',
                    formatDate(lead.VendorLeads?.[0]?.created_at || lead.created_at),
                    lead.VendorLeads?.[0]?.status || 'open'
                ]);

                const csvContent = [
                    headers.join(','),
                    ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.setAttribute("href", url);
                link.setAttribute("download", `Leads_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            console.error("Failed to download CSV data:", err);
            Swal.fire('Error', 'Failed to export filtered leads', 'error');
        } finally {
            setDownloading(false);
        }
    };

    const getAvatarColor = (name) => {
        const char = (name || 'G').charAt(0).toUpperCase();
        const code = char.charCodeAt(0);
        if (code % 3 === 0) return 'from-emerald-500 to-teal-600';
        if (code % 3 === 1) return 'from-orange-400 to-rose-500';
        return 'from-amber-400 to-orange-500';
    };

    const getCategoryBadge = (category) => {
        switch (category) {
            case 'cab':
                return <span className="chip bg-sky-100 text-sky-700 whitespace-nowrap"><i className="fa-solid fa-taxi mr-1"></i>Cab</span>;
            case 'hotels':
                return <span className="chip bg-fuchsia-100 text-fuchsia-700 whitespace-nowrap"><i className="fa-solid fa-hotel mr-1"></i>Hotels</span>;
            case 'holidays':
                return <span className="chip bg-emerald-100 text-emerald-700 whitespace-nowrap"><i className="fa-solid fa-plane mr-1"></i>Holidays</span>;
            case 'tirth_yatra':
                return <span className="chip bg-amber-100 text-amber-700 whitespace-nowrap"><i className="fa-solid fa-dharmachakra mr-1"></i>Tirth Yatra</span>;
            case 'medical_tourism':
                return <span className="chip bg-rose-100 text-rose-700 whitespace-nowrap"><i className="fa-solid fa-briefcase-medical mr-1"></i>Medical Tourism</span>;
            default:
                return <span className="chip bg-gray-100 text-gray-700 whitespace-nowrap text-capitalize">{category.replace('_', ' ')}</span>;
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'open':
                return <span className="chip bg-blue-100 text-blue-700 whitespace-nowrap">Open</span>;
            case 'follow_up':
                return <span className="chip bg-amber-100 text-amber-700 whitespace-nowrap">Follow-up</span>;
            case 'closed':
                return <span className="chip bg-gray-100 text-gray-600 whitespace-nowrap">Closed</span>;
            case 'converted':
                return <span className="chip bg-emerald-100 text-emerald-700 whitespace-nowrap">Converted</span>;
            default:
                return <span className="chip bg-indigo-100 text-indigo-700 whitespace-nowrap text-capitalize">{status}</span>;
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisible = 5;
        let startPage = Math.max(1, pagination.page - 2);
        let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
        
        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="flex items-center gap-1.5 text-xs md:text-sm">
                <button
                    className={`page-btn px-3 py-2 rounded-lg border border-gray-200 bg-white transition ${pagination.page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-50'}`}
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                >
                    <i className="fa-solid fa-chevron-left text-[10px]"></i> Prev
                </button>

                {startPage > 1 && (
                    <>
                        <button
                            className="page-btn px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
                            onClick={() => handlePageChange(1)}
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="px-1 text-gray-400">...</span>}
                    </>
                )}

                {pageNumbers.map(pageNo => (
                    <button
                        key={pageNo}
                        className={`page-btn px-3.5 py-2 rounded-lg transition ${pagination.page === pageNo ? 'active text-white font-bold' : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                        onClick={() => handlePageChange(pageNo)}
                    >
                        {pageNo}
                    </button>
                ))}

                {endPage < pagination.totalPages && (
                    <>
                        {endPage < pagination.totalPages - 1 && <span className="px-1 text-gray-400">...</span>}
                        <button
                            className="page-btn px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
                            onClick={() => handlePageChange(pagination.totalPages)}
                        >
                            {pagination.totalPages}
                        </button>
                    </>
                )}

                <button
                    className={`page-btn px-3 py-2 rounded-lg border border-gray-200 bg-white transition ${pagination.page === pagination.totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-50'}`}
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                >
                    Next <i className="fa-solid fa-chevron-right text-[10px]"></i>
                </button>
            </div>
        );
    };

    const companyName = vendor?.company_name || vendor?.name || 'Vendor Profile';
    const initials = companyName.substring(0, 2).toUpperCase();
    const companyCity = vendor?.company_city || vendor?.city || 'India';

    return (
        <div className="my-leads-page-container">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Top Header */}
                <div className="flex justify-between items-center bg-white px-6 py-4 rounded-2xl border border-white shadow-sm ring-1 ring-black/5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 via-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-extrabold text-xs shadow-md font-display">
                            {initials}
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5 cursor-pointer group">
                                <h1 className="font-extrabold text-[var(--ink)] text-sm md:text-base lg:text-lg group-hover:text-indigo-600 transition">
                                    {companyName}
                                </h1>
                                <i className="fa-solid fa-chevron-down text-[10px] text-gray-400 mt-0.5"></i>
                            </div>
                            <p className="text-[11px] lg:text-xs text-gray-400 flex items-center gap-1">
                                <i className="fa-solid fa-location-dot text-[var(--coral)]"></i> {companyCity}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/admin/dashboard" className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-[var(--ink)] text-xs lg:text-sm rounded-full flex items-center gap-1.5 font-semibold transition">
                            <i className="fa-solid fa-arrow-left"></i> Back to Dashboard
                        </Link>
                        <button className="glow-btn text-white text-xs lg:text-sm px-4 py-2 rounded-full flex items-center gap-1.5 font-semibold transition">
                            <i className="fa-regular fa-circle-question text-base"></i> Help
                        </button>
                    </div>
                </div>

                {/* Main Body */}
                <div className="bg-white p-6 rounded-2xl shadow-sm ring-1 ring-black/5 space-y-8">

                    {/* Overall Performance */}
                    <div>
                        <h2 className="font-extrabold text-[var(--ink)] text-xs md:text-sm lg:text-base mb-4 tracking-wide flex items-center gap-2">
                            <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-indigo-500 to-fuchsia-500"></span>
                            Overall Performance
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">

                            {/* Total Enquiries */}
                            <div className="stat-card bg-gradient-to-br from-indigo-600 to-violet-600 text-white">
                                <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full"></div>
                                <div className="absolute right-3 bottom-3 text-2xl opacity-25"><i class="fa-solid fa-inbox"></i></div>
                                <p className="text-[11px] lg:text-xs text-indigo-100 font-semibold uppercase tracking-wide">Total Enquiries</p>
                                <p className="text-2xl lg:text-3xl font-extrabold mt-1.5 font-display">{pagination.total}</p>
                                <p className="text-[11px] text-indigo-100 mt-1 flex items-center gap-1"><i className="fa-solid fa-arrow-trend-up"></i> steady this month</p>
                            </div>

                            {/* Quotation Request */}
                            <div className="stat-card bg-white border border-amber-100">
                                <span className="absolute top-0 right-0 bg-[var(--marigold)] w-3.5 h-3.5 rounded-bl-full"></span>
                                <p class="text-[11px] lg:text-xs text-gray-400 font-semibold uppercase tracking-wide">Quotation Request</p>
                                <p class="text-2xl lg:text-3xl font-extrabold text-[var(--ink)] mt-1.5 font-display">
                                    {suggestedCount}
                                    <span class="chip text-amber-700 bg-amber-100 ml-1 align-middle">New</span>
                                </p>
                                <div className="mt-3 -mx-4 -mb-4 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-[11px] lg:text-xs text-center font-bold cursor-pointer hover:opacity-95 transition rounded-b-[18px]">
                                    Get Verified Leads <i class="fa-solid fa-chevron-right text-[8px] ml-0.5"></i>
                                </div>
                            </div>

                            {/* Prospect Lead */}
                            <div className="stat-card bg-white border border-emerald-100">
                                <p class="text-[11px] lg:text-xs text-gray-400 font-semibold uppercase tracking-wide">Prospect Lead</p>
                                <div class="flex items-baseline gap-2.5 mt-1.5">
                                    <p class="text-2xl lg:text-3xl font-extrabold text-[var(--ink)] font-display">10sec</p>
                                    <span class="chip text-emerald-700 bg-emerald-100 border border-emerald-200">Good</span>
                                </div>
                                <p class="text-[11px] text-gray-400 mt-2">Avg. response time</p>
                            </div>
                        </div>
                    </div>

                    {/* Promo Banner */}
                    <div className="bg-gradient-to-r from-fuchsia-600 via-violet-600 to-indigo-700 rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center md:justify-between text-white shadow-lg relative overflow-hidden promo-banner">
                        <div className="flex items-center gap-5 z-10">
                            <div className="bg-white/15 backdrop-blur-sm p-2.5 rounded-xl border border-white/25">
                                {vendor?.company_logo_path ? (
                                    <img
                                        src={vendor.company_logo_path}
                                        alt="Logo"
                                        className="w-16 h-16 rounded-lg object-cover shadow-inner bg-white"
                                    />
                                ) : (
                                    <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex flex-col items-center justify-center font-black text-sm leading-none shadow-inner text-white font-display">
                                        <span>TARSIA</span>
                                        <span className="text-[10px] font-normal opacity-90 mt-1.5">INDIA</span>
                                    </div>
                                )}
                            </div>
                            <div>
                                <h4 className="text-xl lg:text-2xl font-extrabold font-display">{companyName}</h4>
                                <p className="text-sm lg:text-base text-violet-100 mt-1.5 flex items-center gap-1.5"><i className="fa-solid fa-location-dot"></i> {companyCity}</p>
                                <p className="text-xs lg:text-sm text-violet-200 mt-2.5 max-w-xl">
                                    Showcase your business with a premium banner and reach thousands of verified customers every day.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 md:mt-0 z-10">
                            <button className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-gray-900 font-bold px-7 py-3.5 rounded-xl shadow-lg transition transform hover:scale-105 w-full md:w-auto">
                                🚀 Get more visibility
                            </button>
                        </div>
                        <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full"></div>
                        <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/10 rounded-full"></div>
                        <div className="absolute right-24 bottom-4 w-16 h-16 bg-white/5 rounded-full hidden lg:block"></div>
                    </div>

                    {/* Recent Enquiries Table Container */}
                    <div className="border border-gray-100 rounded-2xl bg-white shadow-sm overflow-hidden">

                        {/* Card Header with Export Button */}
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white">
                            <h2 className="font-extrabold text-[var(--ink)] text-sm md:text-base lg:text-lg tracking-wide flex items-center gap-2">
                                <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-teal-500 to-emerald-500"></span>
                                Recent Enquiries
                            </h2>
                            {downloading ? (
                                <span className="text-xs lg:text-sm text-gray-400 font-semibold flex items-center gap-1.5">
                                    <i className="fa-solid fa-spinner fa-spin text-[11px]"></i> Exporting...
                                </span>
                            ) : (
                                <button
                                    onClick={handleDownload}
                                    className="text-xs lg:text-sm text-indigo-600 font-semibold cursor-pointer hover:text-indigo-800 flex items-center gap-1.5 bg-none border-none outline-none"
                                >
                                    <i className="fa-solid fa-download text-[11px]"></i> Download
                                </button>
                            )}
                        </div>

                        {/* Filters Panel */}
                        <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex flex-wrap gap-3 items-center">
                            <input
                                type="text"
                                name="name"
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-44 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                placeholder="Search Name..."
                                value={filters.name}
                                onChange={handleFilterChange}
                            />
                            <input
                                type="text"
                                name="email"
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-44 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                placeholder="Search Email..."
                                value={filters.email}
                                onChange={handleFilterChange}
                            />
                            <select
                                name="category"
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-40 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                value={filters.category}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Categories</option>
                                <option value="holidays">Holidays</option>
                                <option value="hotels">Hotels</option>
                                <option value="cab">Cab</option>
                                <option value="tirth_yatra">Tirth Yatra</option>
                                <option value="medical_tourism">Medical Tourism</option>
                            </select>
                            <select
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-40 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                value={filters.status}
                                onChange={(e) => {
                                    setFilters(prev => ({ ...prev, status: e.target.value }));
                                }}
                            >
                                <option value="">All Statuses</option>
                                <option value="open">Open</option>
                                <option value="follow_up">Follow-up</option>
                                <option value="closed">Closed</option>
                                <option value="converted">Converted</option>
                            </select>
                            <div className="w-full sm:w-48">
                                <Flatpickr
                                    className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                    placeholder="Select Date Range"
                                    options={{
                                        mode: "range",
                                        dateFormat: "Y-m-d"
                                    }}
                                    value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : []}
                                    onChange={(dates) => {
                                        if (dates.length === 2) {
                                            const start = getLocalDateString(dates[0]);
                                            const end = getLocalDateString(dates[1]);
                                            setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                                        } else if (dates.length === 0) {
                                            setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
                                        }
                                    }}
                                />
                            </div>
                            <button
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl transition"
                                onClick={() => {
                                    setFilters({ name: '', email: '', category: '', startDate: '', endDate: '', status: '' });
                                    fetchLeads(1);
                                }}
                            >
                                Reset
                            </button>
                        </div>

                        {/* Leads Table */}
                        <div className="overflow-x-auto">
                            {loading ? (
                                <div className="text-center p-8">
                                    <div className="spinner-border text-indigo-600" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="p-6 text-center text-red-500">{error}</div>
                            ) : (
                                <table className="w-full text-left border-collapse leads-table">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="w-12">#</th>
                                            <th>Customer Name</th>
                                            <th>Phone</th>
                                            <th>Email</th>
                                            <th>Category</th>
                                            <th>City</th>
                                            <th>Date Sent</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.length > 0 ? leads.map((lead, index) => {
                                            const startNo = (pagination.page - 1) * pagination.limit;
                                            const avatarLetter = (lead.name || 'G').charAt(0).toUpperCase();
                                            const avatarGrad = getAvatarColor(lead.name);
                                            const isLocked = lead.VendorLeads?.[0]?.is_unlocked === false;

                                            return (
                                                <tr key={lead.id}>
                                                    <td>{startNo + index + 1}</td>
                                                    <td>
                                                        <div className="flex items-center gap-3">
                                                            <div className={`avatar bg-gradient-to-br ${avatarGrad}`}>
                                                                {avatarLetter}
                                                            </div>
                                                            <span className="font-bold text-[var(--ink)]">
                                                                {lead.name || 'Guest'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="text-indigo-600 font-semibold">
                                                        {isLocked ? (
                                                            <span className="flex items-center gap-1.5 whitespace-nowrap text-gray-400">
                                                                <i className="fa-solid fa-phone text-xs rotate-90 opacity-70"></i> ******
                                                            </span>
                                                        ) : (
                                                            <a href={`tel:${lead.mobile}`} className="hover:underline flex items-center gap-1.5 whitespace-nowrap">
                                                                <i className="fa-solid fa-phone text-xs rotate-90 opacity-70"></i> {lead.mobile}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td className="text-gray-500">{lead.email || 'N/A'}</td>
                                                    <td>{getCategoryBadge(lead.category)}</td>
                                                    <td className="text-gray-500">{lead.city || 'N/A'}</td>
                                                    <td className="text-gray-500 text-xs lg:text-sm">
                                                        {formatDate(lead.VendorLeads?.[0]?.created_at || lead.created_at)}
                                                    </td>
                                                    <td>{getStatusChip(lead.VendorLeads?.[0]?.status || 'open')}</td>
                                                    <td>
                                                        <div className="flex items-center gap-2">
                                                            <Link to={`/admin/my-leads/${lead.id}`} className="px-3 py-1.5 bg-indigo-50 text-[var(--indigo)] hover:bg-[var(--indigo)] hover:text-white rounded-lg text-xs font-semibold transition whitespace-nowrap">
                                                                View Details
                                                            </Link>
                                                            {isLocked && (
                                                                <button
                                                                    onClick={() => handleUnlockLead(lead)}
                                                                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold shadow-sm transition whitespace-nowrap flex items-center gap-1"
                                                                >
                                                                    <i className="fa-solid fa-lock text-[10px]"></i> Unlock
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="9" className="text-center p-8 text-gray-500">
                                                    <div className="text-lg font-bold mb-1">No Leads Found</div>
                                                    <div className="text-xs text-gray-400">Try adjusting your filters or dates</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Pagination Footer */}
                        {!loading && pagination.totalPages > 0 && (
                            <div className="px-6 py-4 bg-white border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="text-xs md:text-sm text-gray-500">
                                    Showing <span className="font-semibold text-[var(--ink)]">{pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}</span> to <span className="font-semibold text-[var(--ink)]">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-semibold text-[var(--ink)]">{pagination.total}</span> entries
                                </div>
                                {renderPageNumbers()}
                            </div>
                        )}

                    </div>

                </div>

            </div>
        </div>
    );
}

export default MyLeads;
